import { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const NAV = [
  { id: "home", label: "Home", testid: "nav-home" },
  { id: "services", label: "Services", testid: "nav-services" },
  { id: "about", label: "About", testid: "nav-about" },
  { id: "gallery", label: "Gallery", testid: "nav-gallery" },
  { id: "contact", label: "Contact", testid: "nav-contact" },
];

export default function Header() {
  const { content } = useContent();
  const contact = content?.contact || {};
  const phone = contact.phone || "(254) 833-7563";
  const phoneLink = contact.phone_link || "+12548337563";
  const area = contact.area || "Central Texas";

  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b border-[#E5E7EB] bg-[#F5F7FA] transition-shadow ${scrolled ? "shadow-[0_4px_24px_rgba(0,0,0,0.6)]" : ""}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button onClick={() => scrollTo("home")} className="flex items-center gap-3 group" data-testid="header-logo">
          <div className="w-10 h-10 bg-[#84FB00] flex items-center justify-center">
            <span className="font-display text-2xl leading-none text-[#0A0E1A]">LC</span>
          </div>
          <div className="text-left">
            <div className="font-display text-xl tracking-wider leading-none">LC BUILDERS</div>
            <div className="text-[10px] tracking-[0.3em] text-[#9CA3AF] mt-1">LLC · {area.toUpperCase()}</div>
          </div>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={n.testid}
              onClick={() => scrollTo(n.id)}
              className="font-display tracking-widest text-sm text-[#1F2937] hover:text-[#4AB300] transition-colors uppercase"
            >
              {n.label}
            </button>
          ))}
          <a
            data-testid="nav-call-button"
            href={`tel:${phoneLink}`}
            className="flex items-center gap-2 bg-[#84FB00] hover:bg-[#6EE500] transition-colors px-5 py-3 font-display tracking-widest uppercase text-sm text-[#0A0E1A]"
          >
            <Phone size={16} /> {phone}
          </a>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-[#0A0E1A] p-2"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#E5E7EB] bg-[#F5F7FA]" data-testid="mobile-menu">
          <div className="flex flex-col px-6 py-4 gap-1">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`m-${n.testid}`}
                onClick={() => scrollTo(n.id)}
                className="text-left py-3 font-display tracking-widest uppercase text-[#1F2937] hover:text-[#4AB300] border-b border-[#E5E7EB]"
              >
                {n.label}
              </button>
            ))}
            <a
              href={`tel:${phoneLink}`}
              data-testid="m-nav-call-button"
              className="mt-4 flex items-center justify-center gap-2 bg-[#84FB00] hover:bg-[#6EE500] transition-colors px-5 py-4 font-display tracking-widest uppercase text-[#0A0E1A]"
            >
              <Phone size={16} /> {phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
